import { useState, useCallback, ReactNode, useRef } from 'react';
import {
  ExpandedViewContext,
  ExpandedViewContent,
  ExpandedViewContextType,
} from 'src/shared/components/thread-store/context/ExpandedViewContext';

interface OutlookExpandedViewProviderProps {
  children: ReactNode;
}

/**
 * Outlook-specific implementation of ExpandedViewProvider.
 * Uses Office.context.ui.displayDialogAsync to open expanded views.
 * This provider should wrap content in the Outlook plugin.
 */
export function OutlookExpandedViewProvider({ children }: OutlookExpandedViewProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ExpandedViewContent | null>(null);
  const dialogRef = useRef<Office.Dialog | null>(null);

  const closeExpandedView = useCallback(() => {
    if (dialogRef.current) {
      dialogRef.current.close();
      dialogRef.current = null;
    }
    setIsOpen(false);
    setContent(null);
  }, []);

  const openExpandedView = useCallback(
    (newContent: ExpandedViewContent) => {
      // Close any existing dialog first
      if (dialogRef.current) {
        dialogRef.current.close();
        dialogRef.current = null;
      }

      // Build the URL with query params
      const params = new URLSearchParams();
      params.set('integration', newContent.data.integrationId);
      params.set('thread', newContent.data.threadId);
      params.set('threadStore', newContent.data.threadStoreId);

      if (newContent.data.valueToApproveId) {
        params.set('valueToApproveId', newContent.data.valueToApproveId);
      }

      const url = `${location.origin}/plugins/outlook/taskpane/show-file?${params.toString()}`;

      const dialogOptions: Office.DialogOptions = {
        height: 60,
        width: 70,
        displayInIframe: false,
        promptBeforeOpen: false,
      };

      const handleDialogEvent = (arg: { error: number; type?: Office.EventType }) => {
        if ('error' in arg && arg.error === 12006) {
          // Dialog closed by user
          dialogRef.current = null;
          setIsOpen(false);
          setContent(null);
        }
      };

      const handleMessage = (arg: { message: string; origin: string | undefined } | { error: number }) => {
        if ('message' in arg && arg.message === 'close') {
          closeExpandedView();
        }
      };

      Office.context.ui.displayDialogAsync(url, dialogOptions, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to open dialog:', asyncResult.error.message);
          setIsOpen(false);
          setContent(null);
          return;
        }

        const dialog = asyncResult.value;
        dialogRef.current = dialog;
        setIsOpen(true);
        setContent(newContent);

        try {
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, handleMessage);
          dialog.addEventHandler(Office.EventType.DialogEventReceived, handleDialogEvent as any);
        } catch (error) {
          console.error('Error adding event handlers:', error);
        }
      });
    },
    [closeExpandedView]
  );

  const contextValue: ExpandedViewContextType = {
    openExpandedView,
    closeExpandedView,
    isOpen,
    content,
  };

  return <ExpandedViewContext.Provider value={contextValue}>{children}</ExpandedViewContext.Provider>;
}

export default OutlookExpandedViewProvider;
